import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import "./contractPlan.css";
import Button from "../../../components/button/button";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import { useMantenedora } from "../../../context/MantenedoraContext";
import { getMantenedoraWithPlans, contractPlan } from "../../../services/mantenedora";
import { isOnRequestPlan, planDisplayName } from "../../../components/planChangeModal/planChangeModal";

const formatBRL = (value) =>
  value === null || value === undefined
    ? "Sob consulta"
    : value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatOnboarding = (hours) =>
  hours === null || hours === undefined ? "Sob consulta" : `${hours} hrs`;

const ContractPlan = () => {
  const navigate = useNavigate();
  const { mantenedora, setIsPlanContracted, setIsPlanContractPending } = useMantenedora();

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isContracting, setIsContracting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingData(true);
        const result = await getMantenedoraWithPlans();
        if (result?.plans) {
          const sorted = [...result.plans].sort((a, b) => a.sortOrder - b.sortOrder);
          setPlans(sorted);
        }
      } catch {
        toast.error("Erro ao carregar planos disponíveis.");
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchPlans();
  }, []);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  const handleContract = async () => {
    if (!selectedPlanId) {
      toast.warning("Selecione um plano para continuar.");
      return;
    }

    if (!mantenedora?.id) {
      toast.error("Mantenedora não identificada.");
      return;
    }

    setIsContracting(true);
    try {
      await contractPlan(mantenedora.id, selectedPlanId);
      setIsPlanContracted(true);
      setIsPlanContractPending(true);
      navigate("/app/contratacao-pendente");
    } catch (err) {
      toast.error(err.message || "Erro ao contratar plano. Tente novamente.");
    } finally {
      setIsContracting(false);
    }
  };

  if (isLoadingData) {
    return (
      <div>
        <HeaderTitle title="Contratação de Plano" />
        <div className="mainContentSpace">
          <span>Carregando planos disponíveis...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderTitle title="Contratação de Plano" />

      <div className="mainContentSpace">
        <div className="contract-plan-container">
          <p className="contract-plan-intro">
            Selecione o plano mais adequado para a sua rede de ensino. Após a
            contratação, nossa equipe entrará em contato em até{" "}
            <strong>7 dias úteis</strong> para efetivar o contrato.
          </p>

          <div className="contract-plans-table-wrapper">
            <table className="contract-plans-table">
              <thead>
                <tr>
                  <th className="plan-radio-cell"></th>
                  <th>Plano</th>
                  <th>Número de alunos</th>
                  <th>Onboarding</th>
                  <th>Valor onboarding</th>
                  <th>Valor mensal</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  const isSelected = plan.id === selectedPlanId;
                  return (
                    <tr
                      key={plan.id}
                      className={isSelected ? "is-selected" : ""}
                      onClick={() => setSelectedPlanId(plan.id)}
                    >
                      <td className="plan-radio-cell">
                        <input
                          type="radio"
                          name="planId"
                          value={plan.id}
                          checked={isSelected}
                          onChange={() => setSelectedPlanId(plan.id)}
                        />
                      </td>
                      <td><strong>{planDisplayName(plan)}</strong></td>
                      <td>
                        {isOnRequestPlan(plan) ? "A partir de" : "Até"}{" "}
                        {plan.maxActiveStudents.toLocaleString("pt-BR")} alunos
                      </td>
                      <td>{formatOnboarding(plan.onboardingHours)}</td>
                      <td>{formatBRL(plan.onboardingValue)}</td>
                      <td>{isOnRequestPlan(plan) ? "Sob consulta" : `${formatBRL(plan.monthlyValue)}/mês`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="contract-actions">
            <Button
              text={
                selectedPlan
                  ? `Contratar ${planDisplayName(selectedPlan)}`
                  : "Selecione um plano"
              }
              variation="primary"
              size="big"
              disabled={!selectedPlanId}
              loading={isContracting}
              onClick={handleContract}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractPlan;
